import { Link } from 'react-router-dom'
import useSWR from 'swr'
import Alert from '@/components/ui/Alert'
import { apiGetBillingSummary } from '@/services/BillingService'
import { useTranslation } from 'react-i18next'
import type { BillingSummary } from '@/@types/trackheros'

/**
 * A standing warning while a plan limit is close or reached (DEV-87).
 *
 * Above the route tree on purpose: running out of minutes is not a fact about
 * the page you happen to be on, and a warning that disappears when you navigate
 * is a warning most people will never finish reading.
 *
 * Shares the `['billing', 'summary']` SWR key with the billing page, so
 * visiting that page costs no extra request and dismissing a limit there
 * updates this immediately.
 */

/**
 * Which quota is running out, as translation keys.
 *
 * Keyed by the quota name the API sends, so an unrecognised quota renders no
 * banner at all rather than an English one — a new server-side quota reaching
 * an old build should stay silent, not shout in the wrong language.
 */
const MESSAGE_KEYS: Record<string, { near: string; at100: string }> = {
    meeting_minutes: {
        near: 'billing.usage.minutes80',
        at100: 'billing.usage.minutes100',
    },
    // Storage warns twice before it is full — 75% and 90% — and both read as
    // "almost full"; the bar on the storage page says how close.
    recording_storage_hours: {
        near: 'billing.usage.storage80',
        at100: 'billing.usage.storage100',
    },
    client_count: {
        near: 'billing.usage.clients80',
        at100: 'billing.usage.clients100',
    },
    calendar_count: {
        near: 'billing.usage.calendars80',
        at100: 'billing.usage.calendars100',
    },
    chat_questions: {
        near: 'billing.usage.chat80',
        at100: 'billing.usage.chat100',
    },
}

/** Where the banner's link goes: storage has its own page now. */
const LINKS: Record<string, string> = {
    recording_storage_hours: '/settings/storage',
}

const UsageBanner = () => {
    const { t } = useTranslation()

    const { data } = useSWR<BillingSummary>(
        ['billing', 'summary'],
        () => apiGetBillingSummary(),
        {
            revalidateOnFocus: false,
            // A billing outage must not produce a retry storm on every page.
            shouldRetryOnError: false,
        },
    )

    // Nothing while enforcement is off: the banner says what the plan is about
    // to do, and with the switch off it will not do it. The billing page's
    // meters still show the numbers.
    const warnings = data?.enforcement === false ? [] : (data?.warnings ?? [])
    if (warnings.length === 0) return null

    // The most severe one only. Three stacked banners is a wall people learn to
    // scroll past, which defeats the purpose of warning them at all.
    const worst =
        warnings.find((warning) => warning.endsWith(':100')) ?? warnings[0]
    const [quota, threshold] = worst.split(':')
    const copy = MESSAGE_KEYS[quota]
    if (!copy) return null

    const atLimit = threshold === '100'
    const link = LINKS[quota]

    return (
        <Alert showIcon className="mb-4" type={atLimit ? 'danger' : 'warning'}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span>{t(atLimit ? copy.at100 : copy.near)}</span>
                <Link
                    className="font-semibold hover:underline"
                    to={link ?? '/settings/billing'}
                >
                    {t(
                        link
                            ? 'billing.relief.recordings'
                            : 'billing.viewPlans',
                    )}
                </Link>
            </div>
        </Alert>
    )
}

export default UsageBanner
